<template>
  <div v-if="display" class="informations-card" @click="nextPath()">
    <div v-if="dataType !== 'teams'" class="informations-card__account-number informations-card__account-number--users">
      <Icon class="informations-card__account-number__icon-user">{{ personMedium }}</Icon>
      <span>
        {{ currentData.nUsers }} - {{ $t('message.USERS') }}
      </span>
    </div>
    <div
      v-if="dataType === 'allDatas'"
      class="informations-card__account-number informations-card__account-number--teams"
    >
      <Icon class="informations-card__account-number__icon-team">{{ teamMedium }}</Icon>
      {{ (currentData.teams || []).length }} - {{ $t('message.TEAMS') }}
    </div>
    <div class="informations-card__informations">
      <Icon
        v-if="$route.params.companyId"
        :style="generateIconColor"
        class="informations-card__img"
      >
        {{ cardImg }}
      </Icon>
      <img
        v-else
        :src="companyPicture || 'https://static.thenounproject.com/png/88781-200.png'"
        class="informations-card__img"
        alt
      >
      <p class="informations-card__name">{{ currentData.name || currentData.username }}</p>
      <p class="informations-card__id">{{ $t('message.TEAM_ID', { id: currentData.id }) }}</p>
      <p class="informations-card__status">
        <span
          class="informations-card__status__bullet"
          :style="colorActivity"
        >
          {{ $t('message.BULLET_POINT') }}
        </span>
        {{ isActive }}
      </p>
      <div v-if="dataType === 'teams'">
        <p class="informations-card__numbers">
          <span class="card-title">{{ displayCurrentPage }}</span>
        </p>
        <p class="informations-card__numbers">
          <span class="card-title">{{ displayNavigator }}</span>
        </p>
      </div>
      <p v-if="dataType !== 'teams'" class="informations-card__numbers">
        <span class="informations-card__title">{{ $t('message.ACTIONS') }}{{ $t('message.TWO_DOTS') }}</span>
        {{ displayActions }}
      </p>
      <p v-if="dataType !== 'teams'" class="informations-card__numbers">
        <span class="informations-card__title">{{ $t('message.PROBLEMS') }}{{ $t('message.TWO_DOTS') }}</span>
        {{ displayProblems }}
      </p>
    </div>
  </div>
</template>

<script>
/**
 * Card who will display company / team / user summary informations
 *
 * @vue-prop {Object} [currentData={}] - company / team / user data
 * @vue-prop {String} [dataType=''] - get the current type of data
 * @vue-prop {Boolean} [display=true] - display or hide the card as needed
 */

// Utils & assets
import { UAParser } from 'ua-parser-js';
import { getRandomColor, secondsToArray } from '@/utils/main';
import SPage from '@/utils/classes/SPage';
import { teamMedium, personMedium } from '@/assets/icons';

// Components
import Icon from '@/components/icon.vue';

export default {
  name: 'SummaryInformationsStatsCard',

  components: {
    Icon,
  },

  props: {
    currentData: {
      type: Object,
      required: true,
    },
    dataType: {
      type: String,
      required: true,
    },
    display: {
      type: Boolean,
      default: () => true,
    },
    companyPicture: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      teamMedium,
      personMedium,
    };
  },

  computed: {
    cardImg() {
      if (this.dataType === 'teams') {
        return personMedium;
      } if (this.dataType === 'companies') {
        return teamMedium;
      }
      return '';
    },
    /** String: Return a string with the activity information */
    isActive() {
      const currentTime = this.$moment();
      if (this.currentData.activity && this.currentData.activity.lastActivity) {
        let afkTime = currentTime.diff(this.currentData.activity.lastActivity, 'minutes');
        if (afkTime >= 5) {
          afkTime = secondsToArray(afkTime * 60);
          if (this.dataType === 'allDatas' || this.dataType === 'companies') {
            return `${this.$t('message.STATS_STATUS_AFK_COMPANY')}
            ${this.$t('message.SINCE')} ${afkTime[0]}h ${afkTime[1]}m`;
          }
          return `${this.$t('message.STATS_STATUS_AFK')} ${this.$t('message.SINCE')} ${afkTime[0]}h ${afkTime[1]}m`;
        }
        return `${this.$t('message.STATS_STATUS_ONLINE')}`;
      }
      if (this.currentData.activity && this.currentData.activity.logoutDate) {
        let offlineTime = currentTime.diff(this.currentData.activity.logoutDate, 'minutes');
        offlineTime = secondsToArray(offlineTime * 60);
        if (this.dataType === 'allDatas' || this.dataType === 'companies') {
          return `${this.$t('message.STATS_STATUS_OFFLINE_COMPANY')}
          ${this.$t('message.SINCE')} ${offlineTime[3]}j ${offlineTime[0]}h ${offlineTime[1]}m`;
        }
        return `${this.$t('message.STATS_STATUS_OFFLINE')}
        ${this.$t('message.SINCE')} ${offlineTime[3]}j ${offlineTime[0]}h ${offlineTime[1]}m`;
      }
      if (this.dataType === 'allDatas' || this.dataType === 'companies') {
        return `${this.$t('message.STATS_STATUS_OFFLINE_COMPANY')}`;
      }
      return `${this.$t('message.STATS_STATUS_OFFLINE')}`;
    },
    /** String: return css color for the activity information */
    colorActivity() {
      const currentTime = this.$moment();
      if (this.currentData.activity && this.currentData.activity.lastActivity) {
        const afkTime = currentTime.diff(this.currentData.activity.lastActivity, 'minutes');
        if (afkTime >= 5) {
          return 'color: #F6D94A';
        }
        return 'color: #3f8b3a';
      }
      if (this.currentData.activity && this.currentData.activity.logoutDate) {
        return 'color: #C00F15';
      }
      return 'color: #C00F15';
    },
    /** String: Return the active page name of the user */
    displayCurrentPage() {
      const page = SPage.fromId[this.currentData.activity.pageId];
      if (page) {
        return page.componentName;
      }
      return page;
    },
    /** String: Return the current navigator of the user */
    displayNavigator() {
      if (this.currentData.activity) {
        const parser = new UAParser();
        parser.setUA(this.currentData.activity.userAgent);
        const { name, version } = parser.getBrowser();
        if (!name || !version) {
          return '';
        }
        return `${name} ${version}`;
      }
      return '';
    },
    /** Object: Return an object with data needed for the next page */
    nextPage() {
      switch (this.dataType) {
        case 'allDatas':
          return { name: 'shizenStatsCompany', id: 'companyId' };
        case 'companies':
          return { name: 'shizenStatsTeam', id: 'teamId' };
        default:
          return { name: 'shizenStatsUser', id: 'userId' };
      }
    },
    /** String: Return a string with the number of actions created / finished */
    displayActions() {
      return `
        ${this.$tc('message.VH_AC_ABS_NUMBER_TEXT', this.currentData.sumNActionsCreated, { val: this.currentData.sumNActionsCreated })}${this.$t('message.COMMA')}
        ${this.$tc('message.COMPLETED_ACTIONS_', this.currentData.sumNActionsFinished, { val: this.currentData.sumNActionsFinished })}
      `;
    },
    /** String: Return a string with the number of problems created / finished */
    displayProblems() {
      return `
        ${this.$tc('message.OVERVIEW_CREATED_PB', this.currentData.sumNPbCreated, { n: this.currentData.sumNPbCreated })}${this.$t('message.COMMA')}
        ${this.$tc('message.OVERVIEW_FINISHED_PB', this.currentData.sumNPbFinished, { n: this.currentData.sumNPbFinished })}
      `;
    },
    generateIconColor() {
      const randomColor = getRandomColor();
      if (this.currentData.id) {
        if (sessionStorage.getItem(this.currentData.id)) {
          return `fill:${sessionStorage.getItem(this.currentData.id)}`;
        }
        sessionStorage.setItem(this.currentData.id, randomColor);
      }
      return `fill:${randomColor}`;
    },
  },
  watch: {
    // dataType(newValue) {
    //   if (newValue === 'allDatas') {
    //     this.getCompanyPicture();
    //   }
    // },
  },

  mounted() {
    // this.getCompanyPicture();
  },

  methods: {
    /** Get the company picture from Google CSE
     * Using localStorage to avoid to many API calls for the same picture
    */
    // getCompanyPicture() {
    //   if (this.dataType === 'allDatas') {
    //     const iconQuery = this.currentData.name;
    //     if (!this.companyPicture) {
    //       Vue.axios({
    //         method: 'get',
    //         url: `https://www.googleapis.com/customsearch/v1?key=AIzaSyDP4q19dX75JjN60U1OmGW9bHAE-T7KHh8&cx=2d4e5744e5fca426f&q=${iconQuery}+logo&searchType=image`,
    //       }).then(({ data }) => {
    //         this.companyPicture = data.items[0].link;
    //         localStorage.setItem(`${this.currentData.id}CompanyPicture`, data.items[0].link);
    //       }).catch(({ error }) => {
    //         if (error) {
    //           this.companyPicture = '@/assets/img/company-default-logo.png';
    //         }
    //       });
    //     }
    //   }
    // },
    /** Go to the detailled route */
    nextPath() {
      this.$router.push({
        name: this.nextPage.name,
        params: { [this.nextPage.id]: this.currentData.id },
        query: this.$route.query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars.scss";

.informations-card {
  cursor: pointer;
  transition: 0.3s ease;
  margin: 0 0.5em 1em;
  padding: 3.5em 1.5em 1.8em 1.5em;
  width: calc(100% / 3 - 4em);
  background: white;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background: #2b4cff1e;
  }

  @media (max-width: $smartphone-landscape) {
    width: calc(100% / 3 - 4em);
  }

  @media (max-width: $smartphone-portrait) {
    width: 100%;
    margin: 0 0 1em;
  }

  &__informations {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1em;
  }

  &__account-number {
    position: absolute;
    display: flex;
    align-items: center;
    left: 0.4em;

    &__icon-user {
      width: 1.3em;
      margin-right: 0.2em;
    }

    &__icon-team {
      width: 1.5em;
      margin-right: 0.2em;
    }

    &--users {
      top: 0.2em;
    }

    &--teams {
      top: 2em;
    }
  }

  &__numbers {
    width: 100%;
  }

  &__status {
    margin: 0.5em 0;
    text-align: center;
    position: relative;

    &__bullet {
      font-size: 26px;
      font-weight: bold;
      color: red;
      position: absolute;
      left: -12px;
      top: -8px;

      @media (max-width: $smartphone-landscape) {
        top: -10px;
      }
    }

  }

  &__img {
    border-radius: 100%;
    width: 4em;
    height: 4em;
    object-fit: contain;
    background: #dfdfdf;
  }

  &__title {
    font-weight: bold;
    color: $color-blue-text;
  }

  &__name {
    margin-top: 0.2em;
    font-size: 16px;
    text-align: center;
  }

  &__id {
    font-size: 12px;
    font-style: italic;
  }
}

</style>
